import React, { useMemo, useState } from "react";
import {
  Card,
  Form,
  Switch,
  Button,
  Space,
  Divider,
  Select,
  TimePicker,
  Typography,
  Alert,
  Input,
  Tag,
} from "antd";
import dayjs from "dayjs";

const { Text } = Typography;

const DAYS = [
  { key: "mon", label: "Monday", short: "Mon" },
  { key: "tue", label: "Tuesday", short: "Tue" },
  { key: "wed", label: "Wednesday", short: "Wed" },
  { key: "thu", label: "Thursday", short: "Thu" },
  { key: "fri", label: "Friday", short: "Fri" },
  { key: "sat", label: "Saturday", short: "Sat" },
  { key: "sun", label: "Sunday", short: "Sun" },
];

const DEFAULT_START = "11:00";
const DEFAULT_END = "23:00";

function pad2(n) {
  return String(n).padStart(2, "0");
}

// Convert "HH:mm" <-> dayjs object (no plugins needed)
function toTimeValue(hhmm) {
  if (!hhmm) return null;
  const [h, m] = String(hhmm).split(":");
  return dayjs().hour(Number(h || 0)).minute(Number(m || 0)).second(0);
}
function fromTimeValue(v) {
  if (!v) return "";
  return `${pad2(v.hour())}:${pad2(v.minute())}`;
}

function createDefaultHours() {
  const weekly = {};
  for (const d of DAYS) {
    weekly[d.key] = {
      closed: false,
      periods: [{ start: DEFAULT_START, end: DEFAULT_END }],
    };
  }
  return {
    enabled: true,
    timezone: "", // optional; you can fill "Asia/Kolkata"
    weekly,
  };
}

function normalizeHours(input) {
  const base = createDefaultHours();
  const h = input && typeof input === "object" ? input : {};
  const out = {
    ...base,
    ...h,
    weekly: { ...base.weekly, ...(h.weekly || {}) },
  };

  for (const d of DAYS) {
    const day = out.weekly[d.key] || {};
    const periods = Array.isArray(day.periods) && day.periods.length ? day.periods : base.weekly[d.key].periods;

    out.weekly[d.key] = {
      closed: !!day.closed,
      periods: periods.map((p) => ({
        start: (p?.start || DEFAULT_START).slice(0, 5),
        end: (p?.end || DEFAULT_END).slice(0, 5),
      })),
    };
  }

  return out;
}

function isValidPeriod(p) {
  if (!p?.start || !p?.end) return false;
  const [sh, sm] = p.start.split(":").map(Number);
  const [eh, em] = p.end.split(":").map(Number);
  const startMin = sh * 60 + sm;
  const endMin = eh * 60 + em;
  return endMin > startMin;
}

export default function WorkingHoursSection({ config, onChange }) {
  const hours = useMemo(() => normalizeHours(config?.hours), [config]);

  const updateHours = (mutateFn) => {
    const next = { ...(config || {}) };
    const h = normalizeHours(next.hours);
    mutateFn(h);
    next.hours = h;
    onChange(next);
  };

  // Bulk tools
  const [bulkScope, setBulkScope] = useState("all"); // all | weekdays | weekends
  const [bulkStart, setBulkStart] = useState(toTimeValue(DEFAULT_START));
  const [bulkEnd, setBulkEnd] = useState(toTimeValue(DEFAULT_END));
  const [copyFrom, setCopyFrom] = useState("mon");

  const scopeKeys = useMemo(() => {
    if (bulkScope === "weekdays") return ["mon", "tue", "wed", "thu", "fri"];
    if (bulkScope === "weekends") return ["sat", "sun"];
    return DAYS.map((d) => d.key);
  }, [bulkScope]);

  const applyBulkTimes = () => {
    const start = fromTimeValue(bulkStart) || DEFAULT_START;
    const end = fromTimeValue(bulkEnd) || DEFAULT_END;

    updateHours((h) => {
      for (const k of scopeKeys) {
        const day = h.weekly[k];
        day.closed = false;
        // Keep 1 main period for bulk apply (enterprise behavior: predictable)
        day.periods = [{ start, end }];
      }
    });
  };

  const markScopeClosed = (closed) => {
    updateHours((h) => {
      for (const k of scopeKeys) {
        h.weekly[k].closed = !!closed;
      }
    });
  };

  const copyDayToScope = () => {
    updateHours((h) => {
      const src = h.weekly[copyFrom];
      for (const k of scopeKeys) {
        h.weekly[k] = {
          closed: !!src.closed,
          periods: (src.periods || []).map((p) => ({ ...p })),
        };
      }
    });
  };

  const updateDayClosed = (dayKey, closed) => {
    updateHours((h) => {
      h.weekly[dayKey].closed = !!closed;
    });
  };

  const updatePeriod = (dayKey, idx, patch) => {
    updateHours((h) => {
      const day = h.weekly[dayKey];
      day.periods = day.periods || [{ start: DEFAULT_START, end: DEFAULT_END }];
      const current = day.periods[idx] || { start: DEFAULT_START, end: DEFAULT_END };
      day.periods[idx] = { ...current, ...patch };
    });
  };

  const addPeriod = (dayKey) => {
    updateHours((h) => {
      const day = h.weekly[dayKey];
      day.periods = day.periods || [];
      if (day.periods.length >= 3) return; // keep UX clean (max 3 shifts)
      day.periods.push({ start: DEFAULT_START, end: DEFAULT_END });
    });
  };

  const removePeriod = (dayKey, idx) => {
    updateHours((h) => {
      const day = h.weekly[dayKey];
      const arr = (day.periods || []).slice();
      arr.splice(idx, 1);
      day.periods = arr.length ? arr : [{ start: DEFAULT_START, end: DEFAULT_END }];
    });
  };

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <Card bordered={false}>
        <Form layout="vertical">
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16 }}>Operating Hours</div>
              <Text type="secondary">
                Configure weekly working hours. These will power slot search and “Closed” messaging on the booking experience.
              </Text>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Tag color={hours.enabled ? "green" : "default"}>
                {hours.enabled ? "Enabled" : "Disabled"}
              </Tag>
              <Switch
                checked={!!hours.enabled}
                onChange={(v) =>
                  updateHours((h) => {
                    h.enabled = !!v;
                  })
                }
              />
            </div>
          </div>

          <Divider style={{ margin: "14px 0" }} />

          <Form.Item
            label="Timezone (optional)"
            style={{ maxWidth: 420, marginBottom: 10 }}
          >
            <Input
              placeholder='e.g. "Asia/Kolkata"'
              value={hours.timezone || ""}
              onChange={(e) =>
                updateHours((h) => {
                  h.timezone = e.target.value;
                })
              }
              disabled={!hours.enabled}
            />
            <Text type="secondary">
              Leave blank if you want the system default.
            </Text>
          </Form.Item>

          <Alert
            type="info"
            showIcon
            style={{ marginTop: 10 }}
            message="Bulk actions"
            description={
              <div style={{ display: "grid", gap: 10 }}>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <div style={{ minWidth: 140, fontWeight: 700 }}>Apply to</div>
                  <Select
                    value={bulkScope}
                    onChange={setBulkScope}
                    style={{ width: 180 }}
                    options={[
                      { value: "all", label: "All days" },
                      { value: "weekdays", label: "Weekdays (Mon–Fri)" },
                      { value: "weekends", label: "Weekends (Sat–Sun)" },
                    ]}
                    disabled={!hours.enabled}
                  />

                  <TimePicker
                    value={bulkStart}
                    onChange={setBulkStart}
                    format="hh:mm A"
                    minuteStep={5}
                    use12Hours
                    disabled={!hours.enabled}
                  />
                  <span style={{ opacity: 0.6 }}>to</span>
                  <TimePicker
                    value={bulkEnd}
                    onChange={setBulkEnd}
                    format="hh:mm A"
                    minuteStep={5}
                    use12Hours
                    disabled={!hours.enabled}
                  />

                  <Button type="primary" onClick={applyBulkTimes} disabled={!hours.enabled}>
                    Apply time to scope
                  </Button>

                  <Button onClick={() => markScopeClosed(true)} disabled={!hours.enabled}>
                    Mark scope Closed
                  </Button>

                  <Button onClick={() => markScopeClosed(false)} disabled={!hours.enabled}>
                    Mark scope Open
                  </Button>
                </div>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <div style={{ minWidth: 140, fontWeight: 700 }}>Copy from</div>

                  <Select
                    value={copyFrom}
                    onChange={setCopyFrom}
                    style={{ width: 180 }}
                    options={DAYS.map((d) => ({ value: d.key, label: `${d.label}` }))}
                    disabled={!hours.enabled}
                  />

                  <Button onClick={copyDayToScope} disabled={!hours.enabled}>
                    Copy day schedule to scope
                  </Button>

                  <Text type="secondary">
                    Example: set Monday perfectly → copy to all weekdays.
                  </Text>
                </div>
              </div>
            }
          />
        </Form>
      </Card>

      {/* Weekly Editor */}
      <Card bordered={false}>
        <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>
          Weekly Schedule
        </div>
        <Text type="secondary">
          Add multiple periods if you have breaks (e.g., lunch + dinner). Max 3 periods/day for clean UX.
        </Text>

        <Divider style={{ margin: "14px 0" }} />

        <div style={{ display: "grid", gap: 14 }}>
          {DAYS.map((d) => {
            const day = hours.weekly[d.key];
            const disabled = !hours.enabled;

            return (
              <div
                key={d.key}
                style={{
                  border: "1px solid rgba(0,0,0,0.06)",
                  borderRadius: 12,
                  background: "#fff",
                  padding: 14,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    flexWrap: "wrap",
                    alignItems: "center",
                    marginBottom: 10,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ fontWeight: 900, minWidth: 110 }}>
                      {d.label}
                    </div>
                    {day.closed ? (
                      <Tag color="red">Closed</Tag>
                    ) : (
                      <Tag color="green">Open</Tag>
                    )}
                  </div>

                  <Space size={10} align="center">
                    <Text type="secondary">Closed</Text>
                    <Switch
                      checked={!!day.closed}
                      onChange={(v) => updateDayClosed(d.key, v)}
                      disabled={disabled}
                    />
                  </Space>
                </div>

                {/* Periods */}
                <div style={{ display: "grid", gap: 10, opacity: day.closed ? 0.55 : 1 }}>
                  {(day.periods || []).map((p, idx) => {
                    const ok = isValidPeriod(p);

                    return (
                      <div
                        key={`${d.key}_${idx}`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          flexWrap: "wrap",
                          padding: 10,
                          borderRadius: 10,
                          background: "rgba(15, 23, 42, 0.03)",
                          border: "1px solid rgba(15, 23, 42, 0.06)",
                        }}
                      >
                        <div style={{ fontWeight: 800, minWidth: 80 }}>
                          Period {idx + 1}
                        </div>

                        <TimePicker
                          value={toTimeValue(p.start)}
                          onChange={(v) => updatePeriod(d.key, idx, { start: fromTimeValue(v) })}
                          format="hh:mm A"
                          minuteStep={5}
                          use12Hours
                          disabled={disabled || day.closed}
                        />

                        <span style={{ opacity: 0.6 }}>to</span>

                        <TimePicker
                          value={toTimeValue(p.end)}
                          onChange={(v) => updatePeriod(d.key, idx, { end: fromTimeValue(v) })}
                          format="hh:mm A"
                          minuteStep={5}
                          use12Hours
                          disabled={disabled || day.closed}
                        />

                        {!ok && !day.closed ? (
                          <Text type="danger" style={{ fontSize: 12 }}>
                            End time must be after start time
                          </Text>
                        ) : null}

                        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                          <Button
                            size="small"
                            onClick={() => addPeriod(d.key)}
                            disabled={disabled || day.closed || (day.periods || []).length >= 3}
                          >
                            + Add period
                          </Button>

                          <Button
                            size="small"
                            danger
                            onClick={() => removePeriod(d.key, idx)}
                            disabled={disabled || day.closed || (day.periods || []).length <= 1}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div style={{ marginTop: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Tip: Use bulk apply above for fastest setup, then fine-tune special days here.
                  </Text>
                </div>
              </div>
            );
          })}
        </div>
      </Card>


    </div>
  );
}
